import { inject, Injectable } from '@angular/core';
import { DeliveryWindowForm, DeliveryWindowV1Request } from '@geofences/delivery-windows/delivery-window';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

import { DeliveryWindowsService } from '@core/services/delivery-windows';

@Injectable({ providedIn: 'root' })
export class DeliveryWindowCreateLogic {
  private readonly deliveryWindowsAPIs = inject(DeliveryWindowsService);
  private readonly messageService = inject(MessageService);

  async create(formData: DeliveryWindowForm, geofence: { geofenceId: string; ianaZone: string }): Promise<void> {
    // OrdersEngine builds before MLP-1060 require the zone on create; later builds ignore it and derive it.
    const payload: DeliveryWindowV1Request = {
      ...this.buildDeliveryWindowPayload(formData, geofence),
      ianaZone: geofence.ianaZone
    };

    try {
      await firstValueFrom(this.deliveryWindowsAPIs.create(payload));
      await firstValueFrom(this.deliveryWindowsAPIs.getAll(geofence.geofenceId));
      this.showSuccess('Delivery window created successfully.');
    } catch (error) {
      this.showError('Failed to create delivery window.');
      throw error;
    }
  }

  async edit(formData: DeliveryWindowForm, geofence: { geofenceId: string }, deliveryWindowId: number): Promise<void> {
    const payload = this.buildDeliveryWindowPayload(formData, geofence);

    try {
      await firstValueFrom(this.deliveryWindowsAPIs.updateDeliveryWindow(deliveryWindowId, payload));
      await firstValueFrom(this.deliveryWindowsAPIs.getAll(geofence.geofenceId));
      this.showSuccess('Delivery window updated successfully.');
    } catch (error) {
      this.showError('Failed to update delivery window.');
      throw error;
    }
  }

  private buildDeliveryWindowPayload(
    formData: DeliveryWindowForm,
    geofence: { geofenceId: string }
  ): Omit<DeliveryWindowV1Request, 'ianaZone'> {
    return {
      ...formData,
      startTime: this.formatToHHMM(formData.startTime),
      endTime: this.formatToHHMM(formData.endTime),
      geofenceId: geofence.geofenceId
    };
  }

  private formatToHHMM(dateInput: string | Date): string {
    if (!dateInput) return '';

    // Case 1: Date object
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      return dateInput.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }

    // Case 2: string
    if (typeof dateInput === 'string') {
      const value = dateInput.trim().toLowerCase();

      const ampmMatch = value.match(/^(\d{1,2}):(\d{2})\s?(am|pm)$/);
      if (ampmMatch) {
        let hour = parseInt(ampmMatch[1], 10);
        const minute = ampmMatch[2];
        const period = ampmMatch[3];

        if (period === 'pm' && hour !== 12) hour += 12;
        if (period === 'am' && hour === 12) hour = 0;

        return `${hour.toString().padStart(2, '0')}:${minute}`;
      }

      // "HH:mm"
      const hhmmMatch = value.match(/^(\d{1,2}):(\d{2})$/);
      if (hhmmMatch) {
        return `${hhmmMatch[1].padStart(2, '0')}:${hhmmMatch[2]}`;
      }

      // ISO / full date fallback
      const parsed = new Date(dateInput);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    }

    return '';
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }
}
